import React, { useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { InfoCircleOutlined } from "us.icons";
import "./EditLegalReference.scss";
import {
  REFERENCE_TYPE_OPTIONS,
  ReferenceType,
} from "us.collection/layouts/CaseView/Constants";
import { ValidationSchema } from "./Validations";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import { EntityTypes } from "us.collection.case/constants";
import { domainViewAction } from "us.collection/actions";
import { $Popconfirm } from "us.common/components";
import { DateFormats } from "us.common/constants";

const {
  $Drawer,
  $Button,
  $Form,
  $Row,
  $Col,
  $Select,
  $Input,
  $DatePicker,
  $Alert,
} = Common.Components;

type EditLegalReferenceProps = {
  visible: boolean;
  onClose: () => void;
};

const EditLegalReference: React.FC<EditLegalReferenceProps & PropsFromRedux> = (
  props,
) => {
  const { t } = useTranslation();
  const {
    visible,
    metaData,
    accountSummeryWidgetData,
    legalReference,
    currentDateFormat,
    saveLegalReference,
    onClose,
  } = props;
  const { case: caseDetails } = accountSummeryWidgetData.data ?? {};
  const {
    isEnforcement,
    isSentence,
    caseType,
    referenceNo,
    referenceType,
    nextFollowUpDate,
  } = caseDetails ?? {};

  const referenceTypeOptions = useMemo(() => {
    if (caseType === EntityTypes.CASE) {
      if (isEnforcement && isSentence) {
        return REFERENCE_TYPE_OPTIONS;
      } else if (isSentence) {
        return REFERENCE_TYPE_OPTIONS.filter(
          (option) => option.value !== ReferenceType.ENFORCEMENT,
        );
      } else if (isEnforcement) {
        return REFERENCE_TYPE_OPTIONS.filter(
          (option) => option.value !== ReferenceType.SENTENCE,
        );
      }
    }
    return REFERENCE_TYPE_OPTIONS.filter(
      (option) => option.value === ReferenceType.DEBT_SETTLEMENT,
    );
  }, [metaData, caseDetails]);

  const initialValues = useMemo(() => {
    return {
      referenceType: referenceType || referenceTypeOptions[0]?.value || "",
      referenceNo: referenceNo || "",
      nextFollowUpDate: nextFollowUpDate || "",
    };
  }, [referenceType, referenceNo, nextFollowUpDate, referenceTypeOptions]);

  const handleClose = (resetForm: () => void) => {
    resetForm();
    onClose();
  };

  const handleSubmit = (
    values: typeof initialValues,
    { setSubmitting }: any,
  ) => {
    const { referenceType, referenceNo, nextFollowUpDate } = values;
    const followUpDate = dayjs(nextFollowUpDate).isValid()
      ? dayjs(nextFollowUpDate).format(DateFormats.FORMAT_1)
      : "";
    const params = {
      caseId: caseDetails?.caseId,
      referenceType,
      referenceNo,
      nextFollowUpDate:
        referenceType === ReferenceType.DEBT_SETTLEMENT ? followUpDate : "",
      entityType:
        caseDetails?.caseType === EntityTypes.SUB_CASE
          ? EntityTypes.SUBCASE
          : EntityTypes.CASE,
    };
    saveLegalReference?.(params);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ValidationSchema}
      enableReinitialize
      validateOnBlur
      validateOnChange
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({
        values,
        isValid,
        dirty,
        isSubmitting,
        setFieldValue,
        handleSubmit,
        resetForm,
      }) => (
        <$Drawer
          title={t("US.COLLECTION.CASE:LEGAL_REFERENCE.EDIT_LEGAL_REFERENCE")}
          width={550}
          open={visible}
          onClose={() => handleClose(resetForm)}
          destroyOnClose
        >
          <$Form>
            <$Col span={24}>
              <$Select
                name="referenceType"
                formitem={{
                  label: t("US.COLLECTION.CASE:LEGAL_REFERENCE.REFERENCE_TYPE"),
                }}
                options={referenceTypeOptions}
                optionValue="value"
                optionText="label"
                size="small"
                required
                allOption={false}
                defaultActiveFirstOption={referenceTypeOptions.length === 1}
                className="lr-field-full-width mb-3"
              />
            </$Col>
            <$Col span={24}>
              <$Input
                name="referenceNo"
                label={t("US.COLLECTION.CASE:LEGAL_REFERENCE.REFERENCE_NUMBER")}
                size="small"
                required={true}
                maxLength={15}
              />
            </$Col>

            {values.referenceType === ReferenceType.DEBT_SETTLEMENT && (
              <div className="lr-followup-section">
                <div className="lr-followup-section-title">
                  {t(
                    "US.COLLECTION.CASE:LEGAL_REFERENCE.FOLLOW_UP_CONFIGURATION",
                  )}
                </div>
                <p className="lr-followup-description">
                  {t(
                    "US.COLLECTION.CASE:LEGAL_REFERENCE.FOLLOW_UP_CONFIGURATION_DESCRIPTION",
                  )}
                </p>
                <$Alert
                  message={t(
                    "US.COLLECTION.CASE:LEGAL_REFERENCE.DATE_UPDATE_SCOPE_INFO",
                  )}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  closable
                  className="mb-3"
                />
                <$Row>
                  <$Col span={12}>
                    <$DatePicker
                      name="nextFollowUpDate"
                      label={t(
                        "US.COLLECTION.CASE:LEGAL_REFERENCE.NEXT_FOLLOW_UP_DATE",
                      )}
                      value={
                        values.nextFollowUpDate
                          ? dayjs(values.nextFollowUpDate)
                          : null
                      }
                      size="small"
                      className="lr-field-full-width"
                      required
                      format={currentDateFormat}
                      disabledDate={(current: Dayjs) =>
                        current && current.isBefore(dayjs(), "day")
                      }
                      onSelect={(date: Dayjs) => {
                        setFieldValue(
                          "nextFollowUpDate",
                          date.format(DateFormats.FORMAT_1),
                        );
                      }}
                    />
                  </$Col>
                </$Row>
              </div>
            )}

            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Popconfirm
                  title={t("COMMON.SURE_TO_PROCEED_?")}
                  placement="topLeft"
                  data-testid="pop-confirm"
                  okButtonProps={{
                    dataTestid: "pop-confirm-ok",
                  }}
                  onConfirm={() => handleSubmit()}
                  okText={t("COMMON.YES")}
                  cancelText={t("COMMON.NO")}
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    !dirty ||
                    legalReference.isSaving
                  }
                >
                  <$Button
                    className="mr-2"
                    type="primary"
                    loading={isSubmitting || legalReference.isSaving}
                    disabled={
                      !isValid ||
                      !dirty ||
                      isSubmitting ||
                      legalReference.isSaving
                    }
                    data-testid="legal-reference-save-btn"
                  >
                    {t("US.COMMON:COMMON.SAVE")}
                  </$Button>
                </$Popconfirm>
                <$Button
                  onClick={() => handleClose(resetForm)}
                  data-testid="legal-reference-cancel-btn"
                >
                  {t("US.COMMON:COMMON.CANCEL")}
                </$Button>
              </div>
            </div>
          </$Form>
        </$Drawer>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { dashboard, domainView, common } = state;
  const { currentDateFormat } = common;
  const { accountSummeryWidgetData } = dashboard;
  const { metaData, legalReference } = domainView;
  return {
    accountSummeryWidgetData,
    caseNo: state.case.caseNo,
    metaData,
    legalReference,
    currentDateFormat,
  };
};

const mapDispatchToProps = {
  saveLegalReference: domainViewAction.legalReference.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(EditLegalReference);
